import Batch from 'smg/batch';
import { LineItemMap, TypeMap, SizeMap } from 'smg/customer-api/lineitems';
import * as models from 'smg/customer-api/models';
import { Orders } from 'smg/customer-api/orders';
import { GetQuotes } from 'smg/customer-api/v2/quotes';
import { ShowError } from 'smg/errors';
import { Loader, MultiLoader } from 'smg/multiloader';
import { GetProofURLs } from 'smg/proofs';
import * as session from 'smg/customer-api/session';
import * as template from 'smg/template';
import { DollarFormat } from 'smg/util';
import Big from 'big.js';

import * as carts from '../carts';
import { Relref } from '../hugo';
import { AccountReady } from '../account-session';

declare interface myWindow extends Window {
    readonly cloudFront: string;
    readonly apiRoot: string;
}
declare const window: myWindow;

void main();

// ── Status derivation (shared with orders page) ──

type orderStatus = 'awaiting' | 'preparing' | 'production' | 'shipped' | 'delivered';

function deriveStatus(order: models.HyejooOrder): orderStatus {
    const hasShipments = order.shipmentsCount > 0 || order.markedAsShipped;
    if (hasShipments && order.shipmentsToCustomerNotDeliveredCount === 0) {
        return 'delivered';
    }
    if (hasShipments || order.shipmentsToCustomerNotDeliveredCount > 0) {
        return 'shipped';
    }
    if (order.orderStatusID === models.OrderStatusID.ORDER_STATUSES_CONFIRMED) {
        return 'production';
    }
    if (order.confirmedTime) {
        return 'preparing';
    }
    if (order.orderStatusID === models.OrderStatusID.ORDER_STATUSES_UNCONFIRMED) {
        return 'awaiting';
    }
    return 'preparing';
}

const statusBadge: Record<orderStatus, { cls: string; label: string }> = {
    awaiting: { cls: 's-await', label: 'Awaiting payment' },
    preparing: { cls: 's-prep', label: 'Preparing' },
    production: { cls: 's-prod', label: 'In production' },
    shipped: { cls: 's-ship', label: 'Shipped' },
    delivered: { cls: 's-deliv', label: 'Delivered' },
};

// ── Helpers ──

function escHtml(s: string): string {
    const el = document.createElement('span');
    el.textContent = s;
    return el.innerHTML;
}

function relativeTime(date: { toJSDate(): Date } | Date): string {
    const d = date instanceof Date ? date : date.toJSDate();
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) {
        return diffMins <= 1 ? 'Just now' : `${diffMins} minutes ago`;
    }
    if (diffHours < 24) {
        return diffHours === 1 ? '1 hour ago' : `${diffHours} hours ago`;
    }
    // Check if yesterday
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    if (d.toDateString() === yesterday.toDateString()) {
        return 'Yesterday';
    }
    if (diffDays < 7) {
        return `${diffDays} days ago`;
    }
    // Absolute date
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function relativeTimeShort(date: { toJSDate(): Date }): string {
    const d = date.toJSDate();
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffDays = Math.floor(diffMs / 86400000);
    const diffMonths = Math.floor(diffDays / 30);
    const diffYears = Math.floor(diffDays / 365);

    if (diffDays < 30) {
        return diffDays <= 1 ? 'Today' : `${diffDays} days ago`;
    }
    if (diffMonths < 12) {
        return diffMonths === 1 ? '1 month ago' : `${diffMonths} months ago`;
    }
    return diffYears === 1 ? '1 year ago' : `${diffYears} years ago`;
}

function abbreviateCurrency(n: number): string {
    if (n >= 1_000_000) {
        return `$${(n / 1_000_000).toFixed(1)}M`;
    }
    if (n >= 1_000) {
        return `$${(n / 1_000).toFixed(1)}k`;
    }
    return DollarFormat(n);
}

function greeting(): string {
    const hour = new Date().getHours();
    if (hour >= 5 && hour < 12) {
        return 'Good morning';
    }
    if (hour >= 12 && hour < 17) {
        return 'Good afternoon';
    }
    return 'Good evening';
}

const shortProductNames: Record<string, string> = {
    [models.LineItemID.LINE_ITEMS_LAPEL_PIN]: 'Pins',
    [models.LineItemID.LINE_ITEMS_CHALLENGE_COIN]: 'Coins',
    [models.LineItemID.LINE_ITEMS_CHARM]: 'Charms',
    [models.LineItemID.LINE_ITEMS_KEY_CHAIN]: 'Keychains',
    [models.LineItemID.LINE_ITEMS_LANYARD]: 'Lanyards',
    [models.LineItemID.LINE_ITEMS_MEDAL]: 'Medals',
    [models.LineItemID.LINE_ITEMS_PATCH]: 'Patches',
    [models.LineItemID.LINE_ITEMS_STICKER]: 'Stickers',
    [models.LineItemID.LINE_ITEMS_BUTTON]: 'Buttons',
    [models.LineItemID.LINE_ITEMS_BELT_BUCKLE]: 'Belt Buckles',
    [models.LineItemID.LINE_ITEMS_POKER_CHIP]: 'Poker Chips',
    [models.LineItemID.LINE_ITEMS_T_SHIRT]: 'T-Shirts',
    [models.LineItemID.LINE_ITEMS_WRIST_BAND]: 'Wristbands',
    [models.LineItemID.LINE_ITEMS_ORNAMENT]: 'Ornaments',
};

function shortProductName(lineItemID: string): string {
    return shortProductNames[lineItemID] || '';
}

function orderUrl(order: models.HyejooOrder): string {
    const key = order.key || order.id || '';
    return `${Relref('/order-detail')}?key=${encodeURIComponent(key)}`;
}

function carrierLogoUrl(carrierID: models.CarrierID): string {
    const $logos = document.getElementById('carrier-logos');
    if (!$logos) {
        return '';
    }
    const map: Record<number, string> = {
        [models.CarrierID.CARRIERS_UPS]: $logos.dataset.ups || '',
        [models.CarrierID.CARRIERS_FED_EX]: $logos.dataset.fedex || '',
        [models.CarrierID.CARRIERS_USPS]: $logos.dataset.usps || '',
        [models.CarrierID.CARRIERS_DHL]: $logos.dataset.dhl || '',
        [models.CarrierID.CARRIERS_AMAZON]: $logos.dataset.amazon || '',
    };
    return map[carrierID] || '';
}

// ── Data interfaces ──

const DATA_ALL = [
    'invoiceNumber,created,orderLineItems,orderProofs,orderShippingOptions,total,balance,orderStatusID,key',
    'markedAsShipped,confirmedTime,shipmentsCount,shipmentsToCustomerNotDeliveredCount',
].join(',');

// ── Main ──

async function main() {
    if (await AccountReady) {
        return;
    }
    const sess = await session.Session();

    // Set greeting
    const $greeting = document.getElementById('dashboard-greeting') as HTMLElement;
    const $sub = document.getElementById('dashboard-sub') as HTMLElement;
    $greeting.textContent = `${greeting()}, ${sess.user.firstName ?? ''}`;

    // Fire all data requests in parallel
    const [allOrders, unpaidOrders] = await Promise.all([
        loadAllOrders(),
        loadUnpaidOrders(),
    ]);

    // Derive sections from loaded data
    const delivered = allOrders.filter(o => deriveStatus(o) === 'delivered');
    const active = allOrders.filter(o => deriveStatus(o) !== 'delivered');

    // Sort active by priority: shipped > production > preparing > awaiting
    const statusPriority: Record<orderStatus, number> = {
        shipped: 0,
        production: 1,
        preparing: 2,
        awaiting: 3,
        delivered: 4,
    };
    active.sort((a, b) => statusPriority[deriveStatus(a)] - statusPriority[deriveStatus(b)]);

    // Subtitle
    if (active.length > 0) {
        $sub.textContent = "Here's what's happening with your orders.";
    } else if (allOrders.length > 0) {
        $sub.textContent = 'Everything\'s up to date.';
    } else {
        $greeting.textContent = `Welcome, ${sess.user.firstName ?? ''}`;
        $sub.textContent = 'Let\'s get your first order started.';
    }

    // Section 1: Unpaid banners
    renderUnpaidBanners(unpaidOrders);

    // Section 2: Lifetime stats
    renderLifetimeStats(allOrders);

    // Section 3: Reorder strip
    if (delivered.length > 0) {
        void renderReorderStrip(delivered);
    }

    // Section 4: Active orders
    renderActiveOrders(active);

    // Section 5: Timeline
    initTimeline();
}

// ── Data loading ──

async function loadAllOrders(): Promise<models.HyejooOrder[]> {
    const orders: models.HyejooOrder[] = [];
    let page = 0;
    let hasMore = true;

    while (hasMore) {
        const resp = await Orders({
            sort: 'created desc',
            max: 100,
            p: page,
            data: DATA_ALL,
        }).catch(err => {
            ShowError(err);
            throw err;
        });
        orders.push(...resp.results);
        hasMore = resp.nextPage;
        page++;
    }

    return orders;
}

async function loadUnpaidOrders(): Promise<models.HyejooOrder[]> {
    const resp = await Orders({
        q: 'balance:>.05',
        sort: 'created desc',
        max: 50,
        data: 'invoiceNumber,balance,key',
    }).catch(err => {
        ShowError(err);
        throw err;
    });
    return resp.results;
}

// ── Section 1: Unpaid banners ──

function renderUnpaidBanners(unpaid: models.HyejooOrder[]) {
    if (unpaid.length === 0) {
        return;
    }

    const $container = document.getElementById('unpaid-banners') as HTMLElement;
    $container.classList.remove('d-none');

    for (const order of unpaid) {
        const el = template.Clone('#unpaid-banner-template').firstElementChild as HTMLElement;
        const $text = el.querySelector('.dashboard-action-text') as HTMLElement;
        const balance = Big(order.balance || 0);
        $text.innerHTML = `Order <strong>#${order.invoiceNumber ?? ''}</strong> is awaiting payment &mdash; ${escHtml(DollarFormat(balance))} due`;

        const $link = el.querySelector('.dashboard-action-link') as HTMLAnchorElement;
        $link.href = orderUrl(order);

        $container.appendChild(el);
    }
}

// ── Section 2: Lifetime stats ──

function renderLifetimeStats(orders: models.HyejooOrder[]) {
    const $orders = document.getElementById('stat-orders') as HTMLElement;
    const $pins = document.getElementById('stat-pins') as HTMLElement;
    const $spent = document.getElementById('stat-spent') as HTMLElement;
    const $designs = document.getElementById('stat-designs') as HTMLElement;
    const $since = document.getElementById('lifetime-since') as HTMLElement;

    // Total orders
    $orders.textContent = orders.length.toLocaleString();

    // Product items made — sum of product line item quantities, with dynamic label from distinct plural names
    const $pinsLabel = document.getElementById('stat-pins-label') as HTMLElement;
    let totalItems = 0;
    const productIDs = new Set<string>();
    for (const order of orders) {
        for (const item of order.orderLineItems || []) {
            if (item.lineItemModel?.product) {
                totalItems += item.quantity || 0;
                if (item.lineItemID) {
                    productIDs.add(item.lineItemID);
                }
            }
        }
    }
    $pins.textContent = totalItems.toLocaleString();

    // Build label like "Pins made", "Pins & Coins made", "Pins, Coins, & Lanyards made"
    const names = [...productIDs].map(shortProductName).filter(Boolean);
    if (names.length === 1) {
        $pinsLabel.textContent = `${names[0]} made`;
    } else if (names.length === 2) {
        $pinsLabel.textContent = `${names[0]} & ${names[1]} made`;
    } else if (names.length > 2) {
        $pinsLabel.textContent = `${names.slice(0, -1).join(', ')}, & ${names[names.length - 1]} made`;
    }

    // Total spent
    let totalSpent = Big(0);
    for (const order of orders) {
        totalSpent = totalSpent.plus(order.total || 0);
    }
    $spent.textContent = abbreviateCurrency(totalSpent.toNumber());

    // Unique designs — count distinct proof IDs
    const proofIDs = new Set<string>();
    for (const order of orders) {
        for (const op of order.orderProofs || []) {
            if (op.proofID) {
                proofIDs.add(op.proofID);
            }
        }
    }
    $designs.textContent = proofIDs.size.toLocaleString();

    // Customer since — earliest order date, fallback to "recently"
    if (orders.length > 0) {
        const oldest = orders[orders.length - 1];
        if (oldest.created?.isValid) {
            const d = oldest.created.toJSDate();
            const month = d.toLocaleDateString('en-US', { month: 'short' });
            $since.textContent = `Customer since ${month} ${d.getFullYear()}`;
        }
    }
}

// ── Section 3: Reorder strip ──

async function renderReorderStrip(delivered: models.HyejooOrder[]) {
    const $section = document.getElementById('reorder-section') as HTMLElement;
    const $strip = document.getElementById('reorder-strip') as HTMLElement;
    $section.classList.remove('d-none');

    for (const order of delivered) {
        const el = template.Clone('#reorder-card-template').firstElementChild as HTMLElement;

        // Proofs (32px thumbnails with fancybox)
        const $proofs = el.querySelector('.reorder-card-proofs') as HTMLElement;
        if (order.orderProofs?.length) {
            const fancyboxGroup = `reorder-${order.id ?? ''}`;
            for (const op of order.orderProofs) {
                const proof = op.proof;
                if (!proof) {
                    continue;
                }
                const urls = GetProofURLs(window.cloudFront, proof.file || '');
                if (!urls.thumb && !urls.main) {
                    continue;
                }
                const $link = document.createElement('a');
                $link.href = urls.main;
                $link.setAttribute('data-fancybox', fancyboxGroup);
                const $img = document.createElement('img');
                $img.className = 'reorder-card-thumb';
                $img.src = urls.thumb || urls.main;
                $img.alt = op.name || 'Proof';
                $img.loading = 'lazy';
                $link.appendChild($img);
                $proofs.appendChild($link);
            }
        }

        // Order number link
        const $num = el.querySelector('.reorder-card-num') as HTMLAnchorElement;
        $num.href = orderUrl(order);
        $num.textContent = `#${order.invoiceNumber ?? ''}`;

        // Product name
        const $name = el.querySelector('.reorder-card-name') as HTMLElement;
        const $meta = el.querySelector('.reorder-card-meta') as HTMLElement;

        const firstItem = order.orderLineItems?.[0];
        if (firstItem) {
            const parts = [
                (await TypeMap()).get(firstItem.lineItemTypeID || '')?.networkName() || firstItem.type,
                (await SizeMap()).get(firstItem.lineItemTypeSizeID || '')?.networkName() || firstItem.size,
            ].filter(Boolean);

            const itemName = firstItem.description ||
                ((await LineItemMap()).get(firstItem.lineItemID || '')?.networkNamePlural() || firstItem.lineItem || '');
            $name.textContent = itemName;
            $meta.textContent = [...parts, `\u00d7${(firstItem.quantity || 0).toLocaleString()}`].join(' \u00b7 ');
        }

        // Price
        const $price = el.querySelector('.reorder-card-price') as HTMLElement;
        $price.textContent = DollarFormat(order.total || 0);

        // Last ordered
        const $ago = el.querySelector('.reorder-card-ago') as HTMLElement;
        if (order.created?.isValid) {
            $ago.textContent = relativeTimeShort(order.created);
        }

        // Reorder button
        carts.CartToggle({
            $btn: el.querySelector('.reorder-card-btn') as HTMLButtonElement,
            type: 'order',
            id: order.id ?? '',
            key: order.key || order.id || '',
            addLabel: 'Reorder',
            addIcon: 'shopping_cart',
        });

        $strip.appendChild(el);
    }

    // "View all orders" end card
    const $viewAll = document.createElement('a');
    $viewAll.className = 'dashboard-reorder-viewall';
    $viewAll.href = Relref('/account/orders');
    $viewAll.innerHTML = 'View all orders <i class="material-icons" style="font-size:14px;vertical-align:middle;margin-left:2px">chevron_right</i>';
    $strip.appendChild($viewAll);
}

// ── Section 4: Active orders ──

function renderActiveOrders(active: models.HyejooOrder[]) {
    const $section = document.getElementById('active-section') as HTMLElement;
    const $container = document.getElementById('active-orders') as HTMLElement;

    if (active.length === 0) {
        $section.classList.remove('d-none');
        $container.innerHTML = '<div class="dashboard-active-empty">All caught up &mdash; no active orders right now.</div>';
        return;
    }

    $section.classList.remove('d-none');

    for (const order of active) {
        const status = deriveStatus(order);

        if (status === 'shipped') {
            $container.appendChild(renderShipCard(order));
        } else {
            $container.appendChild(renderMiniCard(order, status));
        }
    }
}

function renderShipCard(order: models.HyejooOrder): HTMLElement {
    const el = template.Clone('#ship-card-template').firstElementChild as HTMLElement;

    const $title = el.querySelector('.dashboard-ship-title') as HTMLElement;
    const url = orderUrl(order);
    $title.innerHTML = `Order <a href="${escHtml(url)}" class="dashboard-ship-order-link">#${order.invoiceNumber ?? ''}</a> is on its way`;

    // Find first shipment with tracking
    let shipment: models.Shipment | undefined;
    for (const oso of order.orderShippingOptions || []) {
        for (const osos of oso.orderShippingOptionShipments || []) {
            if (osos.shipment?.trackingNumber) {
                shipment = osos.shipment;
                break;
            }
        }
        if (shipment) {
            break;
        }
    }

    // Carrier badge — logo if available, text fallback
    const $carrierLogo = el.querySelector('.dashboard-ship-carrier-logo') as HTMLImageElement;
    const $carrierText = el.querySelector('.dashboard-ship-carrier') as HTMLElement;
    const logoUrl = shipment ? carrierLogoUrl(shipment.carrierID) : '';
    if (logoUrl) {
        $carrierLogo.src = logoUrl;
        $carrierLogo.alt = shipment?.carrier?.name || '';
        $carrierLogo.classList.remove('d-none');
        $carrierText.style.display = 'none';
    } else if (shipment?.carrier?.name) {
        $carrierText.textContent = shipment.carrier.name;
    } else {
        $carrierText.style.display = 'none';
    }

    // Progress steps — for shipped orders, mark steps 1-2 as done, step 2 as active
    // Since we don't have real-time tracking status, default to "In transit" for shipped orders
    const steps = el.querySelectorAll('.dashboard-ship-step');
    const activeStep = 2; // Default: in transit
    steps.forEach((step, i) => {
        const dot = step.querySelector('.dashboard-ship-step-dot') as HTMLElement;
        const bar = step.querySelector('.dashboard-ship-bar') as HTMLElement;
        const label = step.querySelector('.dashboard-ship-step-label') as HTMLElement;
        const stepNum = i + 1;

        if (stepNum < activeStep) {
            dot.classList.add('done');
            bar.classList.add('done');
        } else if (stepNum === activeStep) {
            dot.classList.add('active');
            bar.classList.add('done');
            label.classList.add('active');
        }
    });

    // Footer
    const $est = el.querySelector('.dashboard-ship-est') as HTMLElement;
    if (shipment?.deliveredAt?.isValid) {
        const d = shipment.deliveredAt.toJSDate();
        const today = new Date();
        if (d.toDateString() === today.toDateString()) {
            $est.innerHTML = 'Estimated: <strong>Today</strong>';
        } else {
            const formatted = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
            $est.innerHTML = `Estimated: <strong>${escHtml(formatted)}</strong>`;
        }
    }

    const $track = el.querySelector('.dashboard-ship-track') as HTMLAnchorElement;
    if (shipment?.trackingNumber && shipment.carrier?.link) {
        $track.href = shipment.carrier.trackingURL(shipment.trackingNumber);
        $track.textContent = `Track on ${shipment.carrier.name || 'carrier'} \u2192`;
    } else if (shipment?.trackingNumber) {
        $track.style.display = 'none';
    } else {
        $track.style.display = 'none';
    }

    // Make the whole card clickable to order detail
    el.style.cursor = 'pointer';
    el.addEventListener('click', e => {
        if ((e.target as HTMLElement).closest('a')) {
            return;
        }
        window.location.href = orderUrl(order);
    });

    return el;
}

function renderMiniCard(order: models.HyejooOrder, status: orderStatus): HTMLElement {
    const el = template.Clone('#mini-card-template').firstElementChild as HTMLElement;
    const badge = statusBadge[status];

    const $num = el.querySelector('.dashboard-mini-card-num') as HTMLElement;
    const url = orderUrl(order);
    $num.innerHTML = `Order <a href="${escHtml(url)}" class="dashboard-mini-order-link">#${order.invoiceNumber ?? ''}</a>`;

    const $detail = el.querySelector('.dashboard-mini-card-detail') as HTMLElement;
    const firstItem = order.orderLineItems?.[0];
    if (firstItem) {
        const parts = [
            firstItem.lineItem,
            firstItem.type,
            firstItem.size,
            `\u00d7${(firstItem.quantity || 0).toLocaleString()}`,
        ].filter(Boolean);
        $detail.textContent = `${parts.join(' \u00b7 ')} \u2014 ${DollarFormat(order.total || 0)}`;
    } else {
        $detail.textContent = DollarFormat(order.total || 0);
    }

    const $badge = el.querySelector('.status-badge') as HTMLElement;
    $badge.className = `status-badge ${badge.cls}`;
    $badge.textContent = badge.label;

    const $view = el.querySelector('.dashboard-mini-view') as HTMLAnchorElement;
    $view.href = orderUrl(order);

    return el;
}

// ── Section 5: Timeline (MultiLoader) ──

interface timelineRow {
    id: string;
    created: string;
    dotColor: string;
    html: string;
}

function orderTimelineRows(orders: models.HyejooOrder[]): timelineRow[] {
    const rows: timelineRow[] = [];

    for (const order of orders) {
        const num = order.invoiceNumber ?? '';
        const url = orderUrl(order);
        const link = `<a href="${escHtml(url)}" class="dashboard-tl-link">#${num}</a>`;

        // Order placed
        if (order.created?.isValid) {
            rows.push({
                id: `placed-${order.id ?? ''}`,
                created: order.created.toISO() ?? '',
                dotColor: '#1f5697',
                html: `Order <strong>${link}</strong> placed`,
            });
        }

        // Entered production
        if (order.confirmedTime?.isValid) {
            rows.push({
                id: `production-${order.id ?? ''}`,
                created: order.confirmedTime.toISO() ?? '',
                dotColor: '#1f5697',
                html: `Order <strong>${link}</strong> entered production`,
            });
        }

        // Shipped
        const status = deriveStatus(order);
        if (status === 'shipped' || status === 'delivered') {
            for (const oso of order.orderShippingOptions || []) {
                for (const osos of oso.orderShippingOptionShipments || []) {
                    if (osos.shipment?.created?.isValid) {
                        const carrierName = osos.shipment.carrier?.name || '';
                        rows.push({
                            id: `shipped-${osos.shipment.id ?? order.id ?? ''}-${osos.shipment.trackingNumber ?? ''}`,
                            created: osos.shipment.created.toISO() ?? '',
                            dotColor: '#28a745',
                            html: `Order <strong>${link}</strong> shipped${carrierName ? ` via ${escHtml(carrierName)}` : ''}`,
                        });
                    }
                }
            }
        }

        // Delivered
        if (status === 'delivered') {
            for (const oso of order.orderShippingOptions || []) {
                for (const osos of oso.orderShippingOptionShipments || []) {
                    if (osos.shipment?.deliveredAt?.isValid) {
                        rows.push({
                            id: `delivered-${osos.shipment.id ?? order.id ?? ''}`,
                            created: osos.shipment.deliveredAt.toISO() ?? '',
                            dotColor: '#28a745',
                            html: `Order <strong>${link}</strong> delivered`,
                        });
                    }
                }
            }
        }
    }

    return rows;
}

function renderTimelineItem(row: timelineRow): HTMLElement {
    const $item = document.createElement('div');
    $item.className = 'dashboard-tl-item';
    $item.innerHTML = `
        <div class="dashboard-tl-dot-col">
            <div class="dashboard-tl-dot" style="background:${row.dotColor}"></div>
            <div class="dashboard-tl-line"></div>
        </div>
        <div class="dashboard-tl-content">
            <div class="dashboard-tl-text">${row.html}</div>
            <div class="dashboard-tl-time">${escHtml(relativeTime(new Date(row.created)))}</div>
        </div>`;
    return $item;
}

function initTimeline() {
    const $items = document.getElementById('timeline-items') as HTMLElement;
    const $empty = document.getElementById('timeline-empty') as HTMLElement;
    const $loadMore = document.getElementById('timeline-load-more') as HTMLButtonElement;

    let allDone = false;

    const ml = new MultiLoader({
        $target: $items,
        pageSize: 10,
        loaders: [
            // Orders → multiple events per order
            new Loader<timelineRow>({
                name: 'orders',
                load: (b: Batch, page: number, pageSize: number) => Orders({
                    sort: 'created desc',
                    max: pageSize,
                    p: page,
                    data: 'invoiceNumber,created,orderShippingOptions,orderStatusID,key,markedAsShipped,confirmedTime,shipmentsCount,shipmentsToCustomerNotDeliveredCount',
                }).then(resp => ({
                    rows: orderTimelineRows(resp.results),
                    done: !resp.nextPage,
                })),
                element: (row: timelineRow) => renderTimelineItem(row),
            }),

            // Quotes → one event per quote
            new Loader<timelineRow>({
                name: 'quotes',
                load: (b: Batch, page: number, pageSize: number) => GetQuotes({
                    sort: 'created desc',
                    max: pageSize,
                    p: page,
                    data: 'id,serial,created,key',
                }).then(resp => ({
                    rows: resp.results.map(q => ({
                        id: `quote-${q.id ?? ''}`,
                        created: q.created?.toISO() ?? '',
                        dotColor: '#ced4da',
                        html: `New quote <strong><a href="${Relref('/view')}?id=${encodeURIComponent(q.key ?? '')}" class="dashboard-tl-link">#${escHtml(q.serial ?? '')}</a></strong> created`,
                    })),
                    done: !resp.nextPage,
                })),
                element: (row: timelineRow) => renderTimelineItem(row),
            }),
        ],
        done: () => {
            allDone = true;
            $loadMore.classList.add('d-none');
            // If nothing was rendered at all, show empty state
            if (!$items.children.length) {
                $empty.classList.remove('d-none');
            }
        },
    });

    // Initial load — only show "Load more" after load completes if more items exist
    void ml.load().then(() => {
        if (!allDone) {
            $loadMore.classList.remove('d-none');
        }
    });

    // "Load more" button triggers next page
    $loadMore.addEventListener('click', () => {
        void ml.load();
    });
}
